"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  // Particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const r = (a: number, b: number) => a + Math.random() * (b - a);

    interface Particle {
      x: number; y: number; size: number;
      speedX: number; speedY: number;
      opacity: number; fadeSpeed: number;
      fadeDir: number; gold: boolean;
    }

    const particles: Particle[] = Array.from({ length: 90 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: r(0.4, 2.4),
      speedX: r(-0.15, 0.15),
      speedY: r(-0.65, -0.08),
      opacity: r(0.1, 0.7),
      fadeSpeed: r(0.003, 0.01),
      fadeDir: Math.random() > 0.5 ? 1 : -1,
      gold: Math.random() > 0.3,
    }));

    let animId: number;

    const draw = (p: Particle) => {
      const color = p.gold
        ? `rgba(201,168,76,${p.opacity})`
        : `rgba(232,201,106,${p.opacity * 0.45})`;
      ctx.beginPath();
      if (p.size > 1.5) {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = color;
        const s = p.size * 0.85;
        ctx.fillRect(-s / 2, -s / 2, s, s);
        ctx.restore();
      } else {
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.opacity += p.fadeSpeed * p.fadeDir;
        if (p.opacity >= 0.72) p.fadeDir = -1;
        if (p.opacity <= 0.04) p.fadeDir = 1;
        if (p.y < -10) { p.y = canvas.height + 10; p.x = Math.random() * canvas.width; }
        if (p.x < -10 || p.x > canvas.width + 10) p.x = Math.random() * canvas.width;
        draw(p);
      });
      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Mouse parallax
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      setMouse({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#080808] pt-24 pb-20">
      {/* Particles */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />

      {/* Radial glow */}
      <div
        className="animate-glow-pulse absolute inset-0 z-[1] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,168,76,0.055) 0%, transparent 70%)" }}
      />

      {/* Shine sweep */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
        <div
          className="animate-shine-sweep absolute top-0 bottom-0 w-[40%]"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.035), transparent)" }}
        />
      </div>

      {/* Watermark — subtle parallax opposite to bottle */}
      <div
        className="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none overflow-hidden"
        style={{
          transform: `translate(${mouse.x * -10}px, ${mouse.y * -6}px)`,
          transition: "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        }}
      >
        <span
          className="animate-watermark-pulse select-none font-semibold tracking-[0.08em] whitespace-nowrap"
          style={{
            fontSize: "clamp(80px, 18vw, 260px)",
            color: "transparent",
            WebkitTextStroke: "1px rgba(201,168,76,0.07)",
            fontFamily: "Archivo, sans-serif",
          }}
        >
          Parfume
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-4xl w-full">
        {/* Badge */}
        <div
          className="animate-fade-down text-[10px] font-medium tracking-[0.25em] text-accent border border-accent/40 px-4 py-[5px] uppercase mb-7"
          style={{ animationDelay: "200ms", animationFillMode: "both" }}
        >
          Özel Koleksiyon {new Date().getFullYear()}
        </div>

        {/* Parfume */}
        <h1
          className="animate-fade-down font-semibold text-white tracking-[0.06em] leading-none mb-2"
          style={{
            fontSize: "clamp(52px, 10vw, 120px)",
            fontFamily: "Archivo, sans-serif",
            animationDelay: "400ms",
            animationFillMode: "both",
          }}
        >
          Parfume
        </h1>

        {/* Parfüm */}
        <h2
          className="animate-fade-down font-serif italic font-light text-accent leading-none mb-7"
          style={{
            fontSize: "clamp(48px, 9vw, 108px)",
            animationDelay: "600ms",
            animationFillMode: "both",
          }}
        >
          Parfüm
        </h2>

        {/* Divider */}
        <div
          className="animate-fade-in w-16 h-px mb-6"
          style={{
            background: "linear-gradient(90deg, transparent, #C9A84C, transparent)",
            animationDelay: "800ms",
            animationFillMode: "both",
          }}
        />

        {/* Subtitle */}
        <p
          className="animate-fade-in font-serif font-light text-white/55 leading-[1.7] max-w-[380px] mb-10"
          style={{
            fontSize: "clamp(14px, 1.8vw, 18px)",
            animationDelay: "1000ms",
            animationFillMode: "both",
          }}
        >
          Her şişe, bir hikaye. Lüks parfümeri dünyasının en özel notaları — şimdi Türkiye&apos;de.
        </p>

        {/* Buttons */}
        <div
          className="animate-fade-up flex flex-wrap gap-4 items-center justify-center mb-12"
          style={{ animationDelay: "1100ms", animationFillMode: "both" }}
        >
          <Link
            href="/products"
            className="relative overflow-hidden text-[10px] font-semibold tracking-[0.2em] uppercase text-black px-8 py-[14px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(201,168,76,0.4)] group"
            style={{ background: "linear-gradient(135deg, #E8C96A, #C9A84C)" }}
          >
            <span className="relative z-10">Ürünlerimizi Keşfedin</span>
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: "linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.2))" }}
            />
          </Link>
          <Link
            href="/about"
            className="text-[10px] font-semibold tracking-[0.2em] uppercase text-white/75 px-8 py-[13px] border border-white/20 transition-all duration-300 hover:border-accent hover:text-accent hover:-translate-y-0.5"
          >
            Hakkımızda
          </Link>
        </div>

        {/* Floating bottle — outer div handles parallax, inner handles float */}
        <div
          style={{
            transform: `translate(${mouse.x * 7}px, ${mouse.y * 4}px)`,
            transition: "transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          }}
        >
          <div className="animate-bottle-float relative">
            {/* Shadow */}
            <div
              className="animate-shadow-float absolute -bottom-5 left-1/2 -translate-x-1/2 h-5"
              style={{
                background: "radial-gradient(ellipse, rgba(201,168,76,0.35), transparent 70%)",
                filter: "blur(10px)",
                width: 130,
              }}
            />
            {/* Bottle SVG — bigger: w-[130px] */}
            <svg
              className="w-[130px] h-auto drop-shadow-[0_0_24px_rgba(201,168,76,0.22)] hover:drop-shadow-[0_0_44px_rgba(201,168,76,0.52)] transition-[filter] duration-500 cursor-pointer"
              viewBox="0 0 100 160"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Cap */}
              <rect x="38" y="8" width="24" height="8" rx="2" fill="#B8961C" />
              <rect x="40" y="4" width="20" height="6" rx="1.5" fill="#D4AF37" />
              <rect x="44" y="0" width="12" height="5" rx="1" fill="#C9A84C" />
              {/* Neck */}
              <rect x="40" y="16" width="20" height="16" rx="2" fill="url(#neckGrad)" />
              {/* Body */}
              <rect x="22" y="32" width="56" height="112" rx="4" fill="url(#bodyGrad)" />
              {/* Glass highlights */}
              <rect x="26" y="36" width="6" height="100" rx="3" fill="rgba(255,255,255,0.06)" />
              <rect x="68" y="36" width="4" height="100" rx="2" fill="rgba(0,0,0,0.15)" />
              {/* Gold bands */}
              <rect x="22" y="32" width="56" height="6" rx="2" fill="url(#bandGrad)" />
              <rect x="22" y="132" width="56" height="6" rx="2" fill="url(#bandGrad)" />
              {/* Label area */}
              <rect x="28" y="50" width="44" height="74" rx="2" fill="rgba(255,255,255,0.04)" stroke="rgba(201,168,76,0.2)" strokeWidth="0.5" />
              {/* MY — serif italic, gold */}
              <text x="50" y="80" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontSize="14" fontStyle="italic" fill="#C9A84C" fontWeight="400">
                MY
              </text>
              {/* Thin divider line */}
              <line x1="34" y1="86" x2="66" y2="86" stroke="rgba(201,168,76,0.2)" strokeWidth="0.5" />
              {/* Parfüm — small serif */}
              <text x="50" y="100" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontSize="7" fontStyle="italic" fill="rgba(201,168,76,0.75)" fontWeight="300" letterSpacing="1">
                Parfüm
              </text>

              <defs>
                <linearGradient id="bodyGrad" x1="22" y1="0" x2="78" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#1a1a14" />
                  <stop offset="40%" stopColor="#111109" />
                  <stop offset="100%" stopColor="#0d0d08" />
                </linearGradient>
                <linearGradient id="neckGrad" x1="40" y1="0" x2="60" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#C9A84C" />
                  <stop offset="50%" stopColor="#E8C96A" />
                  <stop offset="100%" stopColor="#8B6914" />
                </linearGradient>
                <linearGradient id="bandGrad" x1="22" y1="0" x2="78" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#8B6914" />
                  <stop offset="50%" stopColor="#C9A84C" />
                  <stop offset="100%" stopColor="#8B6914" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
